'use client';

import clsx from 'classnames';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { Button, useOnScreen } from 'ui';

import {
  selectPromotion,
  viewPromotion,
} from '@/modules/google/data-layer/data-layer';
import { ImageAndTextBannerModule } from '@/shared/banners/types';
import { Image } from '@/shared/components/image';

import styles from './ImageAndTextBanner.module.scss';

export function ImageAndTextBanner({
  data,
  imageBreakpoints,
  bannerId,
  name,
  slot,
}: {
  data?: ImageAndTextBannerModule['data'];
  imageBreakpoints: Record<number, number | number[]>;
  bannerId: number;
  name: string;
  slot?: string | null;
}) {
  const ref = useRef(null);
  const { onScreen, disconnect } = useOnScreen(ref);

  const handleLinkClick = () => {
    selectPromotion(name, `P${bannerId}`, slot);
  };

  useEffect(() => {
    if (!onScreen) {
      return;
    }

    disconnect();
    viewPromotion(name, `P${bannerId}`, slot);
  }, [onScreen]);

  if (!data?.imageDesktop?.url || !data?.imageMobile?.url) return null;

  return (
    <Link
      href={data?.url || '#'}
      prefetch={false}
      className={clsx(
        'Banner',
        styles.ImageAndTextBanner__wrapper,
        styles[
          `ImageAndTextBanner__wrapper--${data?.textPosition || 'bottom'}`
        ],
        'ImageAndTextBanner'
      )}
      style={{ background: data?.background || '#ccc' }}
      ref={ref}
      onClick={handleLinkClick}
    >
      <div
        className={clsx(
          styles.ImageAndTextBanner__image,
          'ImageAndTextBanner__image'
        )}
      >
        <Image
          breakpoints={imageBreakpoints}
          containerClassName={styles.ImageAndTextBanner__image__desktop}
          src={`${data.imageDesktop.url}`}
          loader={false}
          width={data.imageDesktop.width}
          height={data.imageDesktop.height}
          forcePortraitSrcSet={
            data.imageDesktop.height > data.imageDesktop.width
          }
          alt={data.imageAlt}
          title={data.imageTitle}
        />
        <Image
          breakpoints={imageBreakpoints}
          containerClassName={styles.ImageAndTextBanner__image__mobile}
          src={`${data.imageMobile.url}`}
          loader={false}
          width={data.imageMobile.width}
          height={data.imageMobile.height}
          forcePortraitSrcSet={data.imageMobile.height > data.imageMobile.width}
          alt={data.imageAlt}
          title={data.imageTitle}
        />
      </div>
      <div
        className={clsx(
          styles.ImageAndTextBanner__content,
          'ImageAndTextBanner__content'
        )}
        style={
          {
            '--module-background': data?.background || '#ccc',
          } as React.CSSProperties
        }
      >
        {!!data?.text1 && (
          <p
            className={clsx(
              styles.ImageAndTextBanner__heading,
              'ImageAndTextBanner__heading'
            )}
            style={{ color: data.color || '#000' }}
          >
            {data.text1}
          </p>
        )}
        {!!data?.text2 && (
          <p
            className={clsx(
              styles.ImageAndTextBanner__subheading,
              'ImageAndTextBanner__subheading'
            )}
            style={{ color: data.color || '#000' }}
          >
            {data.text2}
          </p>
        )}
        {!!data?.text3 && (
          <p
            className={clsx(
              styles.ImageAndTextBanner__text,
              'ImageAndTextBanner__text'
            )}
            style={{ color: data.color || '#000' }}
          >
            {data.text3}
          </p>
        )}
        {!!data?.button && !!data.url && (
          <div
            className={clsx(
              styles.ImageAndTextBanner__button,
              'ImageAndTextBanner__button'
            )}
          >
            <Button
              as="span"
              variant={
                data.buttonStyle === 'outline' ? 'customOutlined' : 'custom'
              }
              color={data.buttonColor || '#000'}
              background={data.buttonBackground || '#ccc'}
            >
              {data.button}
            </Button>
          </div>
        )}
      </div>
    </Link>
  );
}
